import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI, logementAPI, demarcheAPI, guideAPI, messageAPI, notificationAPI, requirementAPI } from '../../service/api';
import { resolveUploadUrl } from '../../config/config';
import { confirmAction, promptReason, showError, showSuccess, toast } from '../../service/swal';
import {
  FiHome, FiUsers, FiGrid, FiFileText, FiBook, FiMessageSquare, FiBell,
  FiPlus, FiEdit2, FiTrash2, FiCheckCircle, FiXCircle, FiSearch,
  FiChevronLeft, FiChevronRight, FiEye, FiShield, FiMail, FiPhone,
  FiMapPin, FiDollarSign, FiClock, FiUser, FiToggleLeft, FiSend,
  FiPaperclip, FiCalendar, FiDownload, FiUpload
} from 'react-icons/fi';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const sidebarItems = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: FiHome },
  { id: 'users', label: 'Utilisateurs', icon: FiUsers },
  { id: 'properties', label: 'Logements', icon: FiGrid },
  { id: 'demarches', label: 'Démarches', icon: FiFileText },
  { id: 'guide', label: 'Guide', icon: FiBook },
  { id: 'messages', label: 'Messages', icon: FiMessageSquare },
  { id: 'requirements', label: 'Exigences', icon: FiFileText },
  { id: 'notifications', label: 'Notifications', icon: FiBell },
];

export default function AdminDashboard({ defaultSection }) {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState(defaultSection || 'overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex bg-parchment">
      <PageMeta title="Administration" />
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-navy text-white transition-all duration-300 flex-shrink-0 overflow-hidden`}>
        <div className="p-4 border-b border-white/10">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/60 hover:text-white">
            {sidebarOpen ? <FiChevronLeft className="w-5 h-5" /> : <FiChevronRight className="w-5 h-5" />}
          </button>
          {sidebarOpen && <p className="text-xs text-white/40 mt-2">Administration</p>}
        </div>
        <nav className="py-2">
          {sidebarItems.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-section={id}
              onClick={() => setActiveSection(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                activeSection === id ? 'bg-brass/20 text-brass border-r-2 border-brass' : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>
      </aside>

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {activeSection === 'overview' && <OverviewSection />}
          {activeSection === 'users' && <UsersSection />}
          {activeSection === 'properties' && <PropertiesSection />}
          {activeSection === 'demarches' && <DemarchesSection />}
          {activeSection === 'guide' && <GuideSection />}
          {activeSection === 'messages' && <MessagesSection />}
          {activeSection === 'requirements' && <RequirementsSection />}
          {activeSection === 'notifications' && <NotificationsSection />}
        </div>
      </div>
    </div>
  );
}

function OverviewSection() {
  const [stats, setStats] = useState({ users: 0, properties: 0, demarches: 0, articles: 0, messages: 0 });
  const [demStats, setDemStats] = useState([]);

  const fetch = useCallback(async () => {
    try {
      const [uRes, pRes, dRes, gRes, mRes, demRes] = await Promise.all([
        userAPI.getAll({ limit: 1 }),
        logementAPI.getAll({ limit: 1 }),
        demarcheAPI.getStats(),
        guideAPI.getAll({ limit: 1 }),
        messageAPI.getThreads(),
        demarcheAPI.getStats(),
      ]);
      setStats({
        users: uRes.data.total || 0,
        properties: pRes.data.total || 0,
        demarches: dRes.data.data?.reduce((a, s) => a + (s.total || 0), 0) || 0,
        articles: gRes.data.total || 0,
        messages: mRes.data.data?.unreadCount || 0,
      });
      setDemStats(demRes.data.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  useAutoRefresh(fetch, 30000);

  const cards = [
    { icon: FiUsers, label: 'Utilisateurs', value: stats.users, color: 'text-blue-500', bg: 'bg-blue-50' },
    { icon: FiGrid, label: 'Logements', value: stats.properties, color: 'text-green-500', bg: 'bg-green-50' },
    { icon: FiFileText, label: 'Démarches', value: stats.demarches, color: 'text-brass', bg: 'bg-yellow-50' },
    { icon: FiBook, label: 'Articles guide', value: stats.articles, color: 'text-purple-500', bg: 'bg-purple-50' },
    { icon: FiMessageSquare, label: 'Messages non lus', value: stats.messages, color: 'text-red-500', bg: 'bg-red-50' },
  ];

  return (
    <div>
      <h1 className="font-serif text-2xl font-bold text-navy mb-1">Tableau de bord</h1>
      <p className="text-navy/60 mb-6">Vue d'ensemble de la plateforme UNIKOL</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {cards.map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} className="card p-5">
            <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center mb-3`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <p className="text-2xl font-bold text-navy">{value}</p>
            <p className="text-xs text-navy/50 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="font-serif font-semibold text-navy mb-4">Répartition des démarches</h2>
          {demStats.length === 0 ? (
            <p className="text-navy/40 text-sm py-8 text-center">Aucune donnée</p>
          ) : (
            <div className="space-y-4">
              {demStats.map((s) => {
                const total = s.total || 1;
                const validePct = Math.round(((s.valide || 0) / total) * 100);
                const coursPct = Math.round(((s.en_cours || 0) / total) * 100);
                const attentePct = Math.round(((s.en_attente || 0) / total) * 100);
                return (
                  <div key={s.type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-navy capitalize">{s.type.replace('_', ' ')}</span>
                      <span className="text-navy/50">{s.total} dossier(s)</span>
                    </div>
                    <div className="h-2 bg-parchment-dark rounded-full overflow-hidden flex">
                      <div className="bg-green-500 h-full" style={{ width: `${validePct}%` }} title={`Validé: ${s.valide || 0}`} />
                      <div className="bg-blue-500 h-full" style={{ width: `${coursPct}%` }} title={`En cours: ${s.en_cours || 0}`} />
                      <div className="bg-yellow-500 h-full" style={{ width: `${attentePct}%` }} title={`Attente: ${s.en_attente || 0}`} />
                    </div>
                    <div className="flex gap-3 mt-1 text-xs text-navy/40">
                      <span className="text-green-600">{s.valide || 0} validé</span>
                      <span className="text-blue-600">{s.en_cours || 0} cours</span>
                      <span className="text-yellow-600">{s.en_attente || 0} attente</span>
                      <span className="text-red-600">{s.rejete || 0} rejeté</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card p-6">
          <h2 className="font-serif font-semibold text-navy mb-4">Actions rapides</h2>
          <div className="space-y-3">
            <button onClick={() => document.querySelector('[data-section="users"]')?.click()}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-parchment-dark hover:bg-parchment transition-colors text-left">
              <FiUsers className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-medium text-navy">Gérer les utilisateurs</span>
            </button>
            <button onClick={() => document.querySelector('[data-section="guide"]')?.click()}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-parchment-dark hover:bg-parchment transition-colors text-left">
              <FiBook className="w-5 h-5 text-purple-500" />
              <span className="text-sm font-medium text-navy">Publier un article guide</span>
            </button>
            <button onClick={() => document.querySelector('[data-section="demarches"]')?.click()}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-parchment-dark hover:bg-parchment transition-colors text-left">
              <FiFileText className="w-5 h-5 text-brass" />
              <span className="text-sm font-medium text-navy">Traiter les démarches</span>
            </button>
            <button onClick={() => document.querySelector('[data-section="properties"]')?.click()}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-parchment-dark hover:bg-parchment transition-colors text-left">
              <FiGrid className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-navy">Vérifier les logements</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const fetchUsers = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (roleFilter) params.role = roleFilter;
      if (search) params.search = search;
      const res = await userAPI.getAll(params);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  }, [page, roleFilter, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useAutoRefresh(() => fetchUsers(true), 30000);

  const handleDelete = async (id, nom) => {
    const confirmed = await confirmAction('Supprimer cet utilisateur ?', `"${nom}" sera définitivement supprimé.`);
    if (!confirmed) return;
    try {
      await userAPI.delete(id);
      fetchUsers();
      showSuccess('Utilisateur supprimé');
    } catch (err) { showError(err.response?.data?.error || 'Erreur'); }
  };

  const roleBadge = (role) => {
    const map = {
      student: 'badge-info', owner: 'badge-warning',
      admin: 'badge-danger', service_acad: 'badge-success',
    };
    const labels = { student: 'Étudiant', owner: 'Propriétaire', admin: 'Admin', service_acad: 'Service Ac.' };
    return <span className={map[role] || 'badge-info'}>{labels[role] || role}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Utilisateurs</h1>
          <p className="text-navy/60 text-sm">{total} utilisateur(s)</p>
        </div>
        <div className="flex gap-3">
          <button className="btn-primary btn-sm" onClick={() => { setEditUser(null); setShowModal(true); }}>
            <FiPlus className="mr-1" /> Nouvel utilisateur
          </button>
        </div>
      </div>

      <div className="card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input className="input-field pl-10" placeholder="Rechercher par nom ou email..."
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="input-field sm:w-44" value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
            <option value="">Tous les rôles</option>
            <option value="student">Étudiant</option>
            <option value="owner">Propriétaire</option>
            <option value="admin">Admin</option>
            <option value="service_acad">Service Académique</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy/5 border-b border-parchment-dark">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-navy">Nom</th>
                <th className="text-left py-3 px-4 font-semibold text-navy">Email</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Rôle</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Inscription</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-parchment-dark">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-navy/40">Chargement...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-navy/40">Aucun utilisateur</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="hover:bg-parchment/50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {u.avatarUrl ? (
                        <img src={u.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center">
                          <FiUser className="w-4 h-4 text-navy/60" />
                        </div>
                      )}
                      <span className="font-medium text-navy">{u.nom}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-navy/60">{u.email}</td>
                  <td className="py-3 px-4 text-center">{roleBadge(u.role)}</td>
                  <td className="py-3 px-4 text-center text-navy/50 text-xs">
                    {new Date(u.dateInscription).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex justify-center gap-2">
                      <button className="p-1.5 rounded hover:bg-parchment text-navy/40 hover:text-brass transition-colors"
                        title="Voir" onClick={() => { setEditUser(u); setShowModal(true); }}>
                        <FiEye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 rounded hover:bg-parchment text-navy/40 hover:text-blue-500 transition-colors"
                        title="Modifier" onClick={() => { setEditUser(u); setShowModal(true); }}>
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 rounded hover:bg-parchment text-navy/40 hover:text-red-500 transition-colors"
                        title="Supprimer" onClick={() => handleDelete(u.id, u.nom)}>
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 15 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button className="btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <FiChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </button>
          <span className="text-sm text-navy/60">Page {page} / {Math.ceil(total / 15)}</span>
          <button className="btn-outline btn-sm" disabled={page >= Math.ceil(total / 15)} onClick={() => setPage(page + 1)}>
            Suivant <FiChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      )}

      {showModal && <UserFormModal user={editUser} onClose={() => setShowModal(false)} onSaved={fetchUsers} />}
    </div>
  );
}

const roleDescriptions = {
  student: 'Accès aux démarches, logements, guide et messagerie',
  owner: 'Gestion des logements et messagerie',
  admin: 'Accès complet au panneau d\'administration',
  service_acad: 'Gestion des dossiers étudiants et démarches',
};

function UserFormModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    nom: user?.nom || '', email: user?.email || '', motDePasse: '', role: user?.role || 'student',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = 'Le nom est requis';
    if (!form.email.trim()) e.email = 'L\'email est requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';
    if (!user && !form.motDePasse) e.motDePasse = 'Le mot de passe est requis';
    else if (!user && form.motDePasse.length < 6) e.motDePasse = '6 caractères minimum';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (user) {
        await userAPI.update(user.id, { nom: form.nom, email: form.email });
      } else {
        await userAPI.create(form);
      }
      onSaved();
      onClose();
      showSuccess(user ? 'Utilisateur modifié' : 'Utilisateur créé');
    } catch (err) { showError(err.response?.data?.error || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}
      style={{ animation: 'fadeIn 0.15s ease' }}>
      <div className="bg-white w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}
        style={{ border: '2px solid #0A192F', animation: 'slideUp 0.2s ease' }}>
        <div className="p-6" style={{ borderBottom: '2px solid #E5E5E0' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#F4F4F0' }}>
                <FiUser className="w-5 h-5" style={{ color: '#0A192F' }} />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>
                  {user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
                </h3>
                <p className="text-xs" style={{ color: '#7A828A' }}>
                  {user ? 'Modifier les informations du compte' : 'Créer un nouveau compte sur la plateforme'}
                </p>
              </div>
            </div>
            <button className="text-2xl" style={{ color: '#7A828A' }} onClick={onClose}>✕</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-4">
            <p className="text-[11px] font-bold tracking-widest uppercase" style={{ color: '#7A828A' }}>Informations personnelles</p>

            <div>
              <label className="label-root mb-1.5">Nom complet</label>
              <input className={`input-root ${errors.nom ? 'border-red-500' : ''}`}
                value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
                placeholder="ex. Jean Mpoyi" required />
              {errors.nom && <p className="text-xs mt-1" style={{ color: '#C62828' }}>{errors.nom}</p>}
            </div>

            <div>
              <label className="label-root mb-1.5">Adresse email</label>
              <input type="email" className={`input-root ${errors.email ? 'border-red-500' : ''}`}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ex. jean@email.com" required />
              {errors.email && <p className="text-xs mt-1" style={{ color: '#C62828' }}>{errors.email}</p>}
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-[11px] font-bold tracking-widest uppercase" style={{ color: '#7A828A' }}>
              {user ? 'Sécurité' : 'Sécurité et rôle'}
            </p>

            {!user && (
              <div>
                <label className="label-root mb-1.5">Mot de passe</label>
                <input type="password" className={`input-root ${errors.motDePasse ? 'border-red-500' : ''}`}
                  value={form.motDePasse} onChange={(e) => setForm({ ...form, motDePasse: e.target.value })}
                  placeholder="Minimum 6 caractères" required minLength={6} />
                {errors.motDePasse && <p className="text-xs mt-1" style={{ color: '#C62828' }}>{errors.motDePasse}</p>}
              </div>
            )}

            {!user && (
              <div>
                <label className="label-root mb-1.5">Rôle</label>
                <div className="space-y-2">
                  {Object.entries({ student: 'Étudiant', owner: 'Propriétaire', admin: 'Administrateur', service_acad: 'Service Académique' }).map(([k, v]) => (
                    <label key={k}
                      className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${form.role === k ? 'bg-[#FAFAF6]' : ''}`}
                      style={{ border: form.role === k ? '2px solid #0A192F' : '2px solid #E5E5E0' }}>
                      <input type="radio" name="role" value={k} checked={form.role === k}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#0A192F' }}>{v}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#7A828A' }}>{roleDescriptions[k]}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {user && (
              <div className="p-3" style={{ backgroundColor: '#F4F4F0', border: '2px solid #E5E5E0' }}>
                <div className="flex items-center gap-2">
                  <FiShield className="w-4 h-4" style={{ color: '#7A828A' }} />
                  <span className="text-sm" style={{ color: '#5A626A' }}>
                    Rôle actuel : <strong className="text-navy capitalize">{user.role.replace('_', ' ')}</strong>
                  </span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#7A828A' }}>{roleDescriptions[user.role]}</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2" style={{ borderTop: '2px solid #E5E5E0' }}>
            <button type="button" className="btn-root flex-1 py-3 text-sm" onClick={onClose}
              style={{ borderColor: '#0A192F', color: '#0A192F' }}>
              Annuler
            </button>
            <button type="submit" className="btn-root flex-1 py-3 text-sm" disabled={loading}
              style={{ backgroundColor: '#0A192F', color: '#F4F4F0', borderColor: '#0A192F' }}>
              {loading ? 'Traitement...' : user ? 'Mettre à jour' : 'Créer le compte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PropertiesSection() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetch = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (typeFilter) params.type = typeFilter;
      if (search) params.search = search;
      const res = await logementAPI.getAll(params);
      setProperties(res.data.properties);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  }, [page, typeFilter, search]);

  useEffect(() => { fetch(); }, [fetch]);
  useAutoRefresh(() => fetch(true), 30000);

  const handleVerify = async (id, val) => {
    try { await logementAPI.update(id, { estVerifie: val }); fetch(); toast('success', val ? 'Logement vérifié' : 'Vérification retirée'); }
    catch (err) { showError('Erreur lors de la vérification'); }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmAction('Supprimer ce logement ?', 'Cette action est irréversible.');
    if (!confirmed) return;
    try { await logementAPI.delete(id); fetch(); showSuccess('Logement supprimé'); }
    catch (err) { showError(err.response?.data?.error || 'Erreur'); }
  };

  const typeLabels = { studio: 'Studio', appartement: 'Appartement', maison: 'Maison', chambre: 'Chambre' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-2xl font-bold text-navy">Logements</h1><p className="text-navy/60 text-sm">{total} logement(s)</p></div>
        <Link to="/logements/nouveau" className="btn-primary"><FiPlus className="w-4 h-4" /> Ajouter un logement</Link>
      </div>

      <div className="card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input className="input-field pl-10" placeholder="Rechercher..." value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="input-field sm:w-40" value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
            <option value="">Tous</option>
            {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy/5 border-b border-parchment-dark">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-navy">Titre</th>
                <th className="text-left py-3 px-4 font-semibold text-navy">Propriétaire</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Type</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Prix</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Statut</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Vérifié</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-parchment-dark">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-navy/40">Chargement...</td></tr>
              ) : properties.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-navy/40">Aucun logement</td></tr>
              ) : properties.map((p) => (
                <tr key={p.id} className="hover:bg-parchment/50 transition-colors">
                  <td className="py-3 px-4">
                    <span className="font-medium text-navy">{p.titre}</span>
                    <p className="text-xs text-navy/40 flex items-center gap-1 mt-0.5"><FiMapPin className="w-3 h-3" />{p.adresse}</p>
                  </td>
                  <td className="py-3 px-4 text-navy/60">{p.ownerName || '—'}</td>
                  <td className="py-3 px-4 text-center"><span className="badge bg-navy/10 text-navy">{typeLabels[p.type] || p.type}</span></td>
                  <td className="py-3 px-4 text-center font-semibold text-brass">{Number(p.prix).toLocaleString()} $</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`badge ${p.statut === 'disponible' ? 'badge-success' : p.statut === 'reserve' ? 'badge-warning' : 'badge-danger'}`}>
                      {p.statut}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {p.estVerifie ? (
                      <span className="badge-success"><FiCheckCircle className="inline w-3 h-3 mr-1" />Vérifié</span>
                    ) : (
                      <span className="badge-warning"><FiClock className="inline w-3 h-3 mr-1" />En attente</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex justify-center gap-2">
                      <Link to={`/logements/${p.id}`} className="p-1.5 rounded hover:bg-parchment text-navy/40 hover:text-brass transition-colors"
                        title="Voir">
                        <FiEye className="w-4 h-4" />
                      </Link>
                      <Link to={`/logements/${p.id}/editer`} className="p-1.5 rounded hover:bg-parchment text-navy/40 hover:text-blue-500 transition-colors"
                        title="Modifier">
                        <FiEdit2 className="w-4 h-4" />
                      </Link>
                      {!p.estVerifie && (
                        <button className="p-1.5 rounded hover:bg-parchment text-navy/40 hover:text-green-500 transition-colors"
                          title="Vérifier" onClick={() => handleVerify(p.id, true)}>
                          <FiCheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button className="p-1.5 rounded hover:bg-parchment text-navy/40 hover:text-red-500 transition-colors"
                        title="Supprimer" onClick={() => handleDelete(p.id)}>
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 15 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button className="btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <FiChevronLeft className="w-4 h-4 mr-1" /> Précédent
          </button>
          <span className="text-sm text-navy/60">Page {page} / {Math.ceil(total / 15)}</span>
          <button className="btn-outline btn-sm" disabled={page >= Math.ceil(total / 15)} onClick={() => setPage(page + 1)}>
            Suivant <FiChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
}

const fileIcons = {
  pdf: '📄', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
  doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
};
function getFileIcon(filename) {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return fileIcons[ext] || '📎';
}

function DemarchesSection() {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [viewStep, setViewStep] = useState(null);
  const [stepDetail, setStepDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const adminFileInputRef = useRef(null);

  const fetch = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      if (filter) params.statutCourant = filter;
      if (typeFilter) params.type = typeFilter;
      if (search) params.search = search;
      const res = await demarcheAPI.getAllAdmin(params);
      setSteps(res.data.steps);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  }, [filter, typeFilter, search]);

  useEffect(() => { fetch(); }, [fetch]);
  useAutoRefresh(() => fetch(true), 30000);

  const handleAdminFileUpload = async (stepId, files) => {
    const formData = new FormData();
    for (const f of files) formData.append('fichiers', f);
    try {
      await demarcheAPI.uploadDocument(stepId, formData);
      const r = await demarcheAPI.getById(stepId);
      setStepDetail(r.data.data);
      toast('success', 'Document ajouté');
    } catch (err) { showError('Erreur lors de l\'upload'); }
  };

  const handleStatus = async (id, statutCourant) => {
    let commentaire = '';
    if (statutCourant === 'rejete') {
      commentaire = await promptReason('Motif du rejet');
      if (commentaire === null) return;
    }
    try { await demarcheAPI.updateStatus(id, { statutCourant, commentaire }); fetch(); toast('success', `Statut mis à jour`); }
    catch (err) { showError('Erreur lors de la mise à jour'); }
  };

  const openDossier = async (id) => {
    setViewStep(id);
    setDetailLoading(true);
    try { const r = await demarcheAPI.getById(id); setStepDetail(r.data.data); }
    catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  };

  const labels = { visa: 'Visa', academic: 'Inscription', insurance: 'Assurance', residence_permit: 'Carte de séjour' };
  const statusBadge = (s) => {
    const map = {
      en_attente: 'badge-warning', en_cours: 'badge-info',
      valide: 'badge-success', rejete: 'badge-danger',
    };
    return <span className={map[s]}>{s.replace('_', ' ')}</span>;
  };

  const statusConfig = {
    en_attente: { color: '#D4A017', bg: '#FFF8E0' },
    en_cours: { color: '#1565C0', bg: '#E3F0FF' },
    valide: { color: '#2E7D32', bg: '#E8F5E9' },
    rejete: { color: '#C62828', bg: '#FFEBEE' },
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-2xl font-bold text-navy">Démarches</h1><p className="text-navy/60 text-sm">{steps.length} dossier(s)</p></div>
      </div>

      <div className="card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input className="input-field pl-10" placeholder="Rechercher par étudiant..." value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="input-field sm:w-40" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Tous les types</option>
            {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="input-field sm:w-40" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="en_attente">En attente</option>
            <option value="en_cours">En cours</option>
            <option value="valide">Validé</option>
            <option value="rejete">Rejeté</option>
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy/5 border-b border-parchment-dark">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-navy">Étudiant</th>
                <th className="text-left py-3 px-4 font-semibold text-navy">Type</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Statut</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Documents</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Créé le</th>
                <th className="text-center py-3 px-4 font-semibold text-navy">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-parchment-dark">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-navy/40">Chargement...</td></tr>
              ) : steps.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-navy/40">Aucune démarche</td></tr>
              ) : steps.map((s) => (
                <tr key={s.id} className="hover:bg-parchment/50 transition-colors">
                  <td className="py-3 px-4">
                    <span className="font-medium text-navy">{s.studentName}</span>
                    <p className="text-xs text-navy/40">{s.numeroPasseport}</p>
                  </td>
                  <td className="py-3 px-4"><span className="font-medium text-navy">{labels[s.type] || s.type}</span></td>
                  <td className="py-3 px-4 text-center">{statusBadge(s.statutCourant)}</td>
                  <td className="py-3 px-4 text-center text-navy/60">{s.documentCount || 0}</td>
                  <td className="py-3 px-4 text-center text-xs text-navy/50">{new Date(s.dateCreation).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    <div className="flex justify-center gap-2">
                      <button className="p-1.5 rounded hover:bg-navy/5 text-brass transition-colors"
                        title="Voir le dossier" onClick={() => openDossier(s.id)}>
                        <FiEye className="w-4 h-4" />
                      </button>
                      {s.statutCourant !== 'valide' && (
                        <button className="p-1.5 rounded hover:bg-green-50 text-green-600 transition-colors"
                          title="Valider" onClick={() => handleStatus(s.id, 'valide')}>
                          <FiCheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {s.statutCourant !== 'rejete' && s.statutCourant !== 'valide' && (
                        <button className="p-1.5 rounded hover:bg-red-50 text-red-600 transition-colors"
                          title="Rejeter" onClick={() => handleStatus(s.id, 'rejete')}>
                          <FiXCircle className="w-4 h-4" />
                        </button>
                      )}
                      {s.statutCourant === 'en_attente' && (
                        <button className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                          title="Marquer en cours" onClick={() => handleStatus(s.id, 'en_cours')}>
                          <FiClock className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setViewStep(null)}
          style={{ animation: 'fadeIn 0.15s ease' }}>
          <div className="bg-white w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ border: '2px solid #0A192F', animation: 'slideUp 0.2s ease' }}>
            <div className="sticky top-0 bg-white z-10" style={{ borderBottom: '2px solid #E5E5E0' }}>
              <div className="flex items-center justify-between p-6">
                <div className="flex items-center gap-3">
                  <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>
                    Dossier {labels[stepDetail?.type] || stepDetail?.type}
                  </h3>
                  {stepDetail && (
                    <span className="text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2"
                      style={{ color: (statusConfig[stepDetail.statutCourant] || {}).color, borderColor: (statusConfig[stepDetail.statutCourant] || {}).color, backgroundColor: (statusConfig[stepDetail.statutCourant] || {}).bg }}>
                      {stepDetail.statutCourant?.replace('_', ' ')}
                    </span>
                  )}
                </div>
                <button className="text-2xl" style={{ color: '#7A828A' }} onClick={() => setViewStep(null)}>✕</button>
              </div>
              {stepDetail && (
                <div className="px-6 pb-4 flex items-center gap-6 text-sm" style={{ color: '#5A626A' }}>
                  <div className="flex items-center gap-2">
                    <FiUser className="w-4 h-4" style={{ color: '#7A828A' }} />
                    <span className="font-semibold" style={{ color: '#0A192F' }}>{stepDetail.studentName}</span>
                  </div>
                  {stepDetail.numeroPasseport && (
                    <span className="text-xs" style={{ color: '#7A828A' }}>Passeport: {stepDetail.numeroPasseport}</span>
                  )}
                  {stepDetail.nationalite && (
                    <span className="text-xs" style={{ color: '#7A828A' }}>{stepDetail.nationalite}</span>
                  )}
                  <span className="text-xs" style={{ color: '#7A828A' }}>
                    Créé le {new Date(stepDetail.dateCreation).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {detailLoading ? (
              <div className="p-12 text-center text-sm" style={{ color: '#7A828A' }}>Chargement...</div>
            ) : stepDetail ? (
              <div className="p-6 space-y-8">

                {/* Documents */}
                <div>
                  <h4 className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-2" style={{ color: '#7A828A' }}>
                    <FiPaperclip className="w-4 h-4" /> Documents soumis ({stepDetail.documents?.length || 0})
                  </h4>
                  {(!stepDetail.documents || stepDetail.documents.length === 0) ? (
                    <div className="py-8 text-center" style={{ border: '2px dashed #E5E5E0' }}>
                      <FiFileText className="w-10 h-10 mx-auto mb-2" style={{ color: '#E5E5E0' }} />
                      <p className="text-sm" style={{ color: '#7A828A' }}>Aucun document soumis</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(stepDetail.documents || []).map((doc) => {
                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(doc.fichierUrl);
                        return (
                          <div key={doc.id} className="p-3" style={{ border: '2px solid #E5E5E0', backgroundColor: '#FAFAF6' }}>
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">{isImage ? '🖼️' : getFileIcon(doc.nomFichier)}</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold truncate" style={{ color: '#0A192F' }}
                                  title={doc.nomFichier || ''}>{doc.nomFichier || 'Sans nom'}</p>
                                <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 mt-1 ${doc.typeDocument === 'document_admin' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {doc.typeDocument === 'document_admin' ? 'Administration' : 'Étudiant'}
                                </span>
                                <p className="text-xs mt-1" style={{ color: '#7A828A' }}>
                                  {new Date(doc.dateTelechargement).toLocaleDateString()}
                                </p>
                                <div className="flex gap-2 mt-2">
                                  <a href={resolveUploadUrl(doc.fichierUrl)} target="_blank" rel="noopener noreferrer"
                                    className="btn-root text-xs px-3 py-1" style={{ borderColor: '#0A192F', color: '#0A192F', fontSize: '10px' }}>
                                    <FiEye className="w-3 h-3 mr-1" /> Voir
                                  </a>
                                  <a href={resolveUploadUrl(doc.fichierUrl)} download
                                    className="btn-root text-xs px-3 py-1" style={{ borderColor: '#0A192F', color: '#0A192F', fontSize: '10px' }}>
                                    <FiDownload className="w-3 h-3 mr-1" /> Télécharger
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                  <div className="mt-3">
                    <input type="file" multiple ref={adminFileInputRef}
                      onChange={(e) => { if (e.target.files.length) handleAdminFileUpload(stepDetail.id, e.target.files); }}
                      className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                    <button className="btn-root text-xs px-3 py-1 flex items-center gap-1"
                      style={{ borderColor: '#0A192F', color: '#0A192F' }}
                      onClick={() => adminFileInputRef.current?.click()}>
                      <FiUpload className="w-3 h-3" /> Ajouter un document
                    </button>
                  </div>

                {/* Timeline */}
                <div>
                  <h4 className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-2" style={{ color: '#7A828A' }}>
                    <FiCalendar className="w-4 h-4" /> Historique du dossier
                  </h4>
                  {(stepDetail.history || []).length === 0 ? (
                    <div className="py-8 text-center" style={{ border: '2px dashed #E5E5E0' }}>
                      <FiClock className="w-10 h-10 mx-auto mb-2" style={{ color: '#E5E5E0' }} />
                      <p className="text-sm" style={{ color: '#7A828A' }}>Aucun historique</p>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {(stepDetail.history || []).map((h, i) => {
                        const hCfg = statusConfig[h.statut] || statusConfig.en_attente;
                        const isLast = i === (stepDetail.history || []).length - 1;
                        return (
                          <div key={h.id} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: hCfg.color }} />
                              {!isLast && <div className="w-0.5 flex-1" style={{ backgroundColor: '#E5E5E0' }} />}
                            </div>
                            <div className="pb-4">
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold tracking-widest uppercase" style={{ color: hCfg.color }}>
                                  {h.statut.replace('_', ' ')}
                                </span>
                                <span className="text-xs" style={{ color: '#7A828A' }}>
                                  {new Date(h.dateModification).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              {h.commentaire && (
                                <p className="text-sm mt-1" style={{ color: '#5A626A' }}>{h.commentaire}</p>
                              )}
                              {h.modifieParNom && (
                                <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: '#7A828A' }}>
                                  <FiUser className="w-3 h-3" /> Par {h.modifieParNom}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4" style={{ borderTop: '2px solid #E5E5E0' }}>
                  {stepDetail.statutCourant !== 'valide' && (
                    <button className="btn-root px-5 py-3 text-sm font-semibold"
                      style={{ borderColor: '#2E7D32', color: '#2E7D32' }}
                      onClick={async () => {
                        const confirmed = await confirmAction('Valider ce dossier ?', 'Le statut passera à "Validé".');
                        if (!confirmed) return;
                        await demarcheAPI.updateStatus(stepDetail.id, { statutCourant: 'valide' });
                        setViewStep(null); fetch(); toast('success', 'Dossier validé');
                      }}>
                      <FiCheckCircle className="mr-2 w-4 h-4" /> Valider le dossier
                    </button>
                  )}
                  {stepDetail.statutCourant !== 'rejete' && stepDetail.statutCourant !== 'valide' && (
                    <button className="btn-root px-5 py-3 text-sm font-semibold"
                      style={{ borderColor: '#C62828', color: '#C62828' }}
                      onClick={async () => {
                        const c = await promptReason('Motif du rejet');
                        if (c) { await demarcheAPI.updateStatus(stepDetail.id, { statutCourant: 'rejete', commentaire: c }); setViewStep(null); fetch(); toast('success', 'Dossier rejeté'); }
                      }}>
                      <FiXCircle className="mr-2 w-4 h-4" /> Rejeter le dossier
                    </button>
                  )}
                  {stepDetail.statutCourant === 'en_attente' && (
                    <button className="btn-root px-5 py-3 text-sm font-semibold"
                      style={{ borderColor: '#1565C0', color: '#1565C0' }}
                      onClick={async () => {
                        await demarcheAPI.updateStatus(stepDetail.id, { statutCourant: 'en_cours' });
                        setViewStep(null); fetch();
                      }}>
                      <FiClock className="mr-2 w-4 h-4" /> Marquer en cours
                    </button>
                  )}
                </div>

              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function GuideSection() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [catFilter, setCatFilter] = useState('');

  const fetch = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = {};
      if (catFilter) params.categorie = catFilter;
      const [aRes, cRes] = await Promise.all([guideAPI.getAll(params), guideAPI.getCategories()]);
      setArticles(aRes.data.articles);
      setCategories(cRes.data.data || []);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  }, [catFilter]);

  useEffect(() => { fetch(); }, [fetch]);
  useAutoRefresh(() => fetch(true), 30000);

  const handleDelete = async (id) => {
    const confirmed = await confirmAction('Supprimer cet article ?', 'Cette action est irréversible.');
    if (!confirmed) return;
    try { await guideAPI.delete(id); fetch(); showSuccess('Article supprimé'); }
    catch (err) { showError('Erreur lors de la suppression'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-2xl font-bold text-navy">Guide de Kolwezi</h1><p className="text-navy/60 text-sm">{articles.length} article(s)</p></div>
        <button className="btn-primary btn-sm" onClick={() => { setEditArticle(null); setShowModal(true); }}>
          <FiPlus className="mr-1" /> Nouvel article
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        <button className={`px-3 py-1.5 text-xs font-bold tracking-widest uppercase transition-all ${!catFilter ? 'bg-navy text-white' : 'bg-white text-navy border-2 border-navy'}`}
          onClick={() => setCatFilter('')}>Tous</button>
        {categories.map((c) => (
          <button key={c.categorie} className={`px-3 py-1.5 text-xs font-bold tracking-widest uppercase transition-all ${catFilter === c.categorie ? 'bg-navy text-white' : 'bg-white text-navy border-2 border-navy'}`}
            onClick={() => setCatFilter(c.categorie)}>
            {catIcons[c.categorie] || '📌'} {catLabels[c.categorie] || c.categorie} ({c.count})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-brass border-t-transparent" /></div>
        ) : articles.length === 0 ? (
          <div className="col-span-full text-center py-12 text-navy/40">Aucun article</div>
        ) : articles.map((a) => (
          <div key={a.id} className="card p-5">
            <div className="flex items-start justify-between mb-2">
              <span className="text-xl">{catIcons[a.categorie] || '📌'}</span>
              <div className="flex gap-1">
                <button className="p-1 rounded hover:bg-parchment text-navy/40 hover:text-blue-500 transition-colors"
                  onClick={() => { setEditArticle(a); setShowModal(true); }}>
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
                <button className="p-1 rounded hover:bg-parchment text-navy/40 hover:text-red-500 transition-colors"
                  onClick={() => handleDelete(a.id)}>
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <h3 className="font-serif font-semibold text-navy text-sm mb-1">{a.titre}</h3>
            <p className="text-xs text-navy/50 line-clamp-2">{a.contenu}</p>
            <div className="flex justify-between items-center mt-3 text-xs text-navy/40">
              <span>{catLabels[a.categorie] || a.categorie}</span>
              <span>{new Date(a.dateCreation).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>

      {showModal && <GuideFormModal article={editArticle} onClose={() => setShowModal(false)} onSaved={fetch} categories={categories} />}
    </div>
  );
}

const catIcons = { transport: '🚌', sante: '🏥', banque: '🏦', telecom: '📱', logement: '🏠', administration: '📋', education: '🎓', autre: '📌' };
const catLabels = { transport: 'Transports', sante: 'Santé', banque: 'Banques', telecom: 'Télécoms', logement: 'Logement', administration: 'Administration', education: 'Éducation', autre: 'Autre' };

function GuideFormModal({ article, onClose, onSaved, categories }) {
  const [form, setForm] = useState({
    titre: article?.titre || '', contenu: article?.contenu || '',
    categorie: article?.categorie || 'transport', estPublie: article?.estPublie !== false,
  });
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim() || !form.contenu.trim()) return;
    setLoading(true);
    try {
      if (article) { await guideAPI.update(article.id, form); }
      else { await guideAPI.create(form); }
      onSaved(); onClose(); showSuccess(article ? 'Article modifié' : 'Article créé');
    } catch (err) { showError('Erreur lors de l\'enregistrement'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}
      style={{ animation: 'fadeIn 0.15s ease' }}>
      <div className="bg-white w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ border: '2px solid #0A192F', animation: 'slideUp 0.2s ease' }}>
        <div className="p-6" style={{ borderBottom: '2px solid #E5E5E0' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#F4F4F0' }}>
                <FiBook className="w-5 h-5" style={{ color: '#0A192F' }} />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>
                  {article ? 'Modifier l\'article' : 'Nouvel article'}
                </h3>
                <p className="text-xs" style={{ color: '#7A828A' }}>
                  {article ? 'Modifier le contenu du guide' : 'Ajouter un article au guide de Kolwezi'}
                </p>
              </div>
            </div>
            <button className="text-2xl" style={{ color: '#7A828A' }} onClick={onClose}>✕</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-4">
            <p className="text-[11px] font-bold tracking-widest uppercase" style={{ color: '#7A828A' }}>Informations générales</p>

            <div>
              <label className="label-root mb-1.5">Titre de l'article</label>
              <input className="input-root" value={form.titre}
                onChange={(e) => setForm({ ...form, titre: e.target.value })}
                placeholder="ex. Se déplacer à Kolwezi" required />
            </div>

            <div>
              <label className="label-root mb-1.5">Catégorie</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(catLabels).map(([k, v]) => (
                  <button key={k} type="button"
                    className={`p-3 text-center transition-colors ${form.categorie === k ? 'bg-[#0A192F] text-[#F4F4F0]' : 'bg-[#F4F4F0] text-[#0A192F]'}`}
                    onClick={() => setForm({ ...form, categorie: k })}
                    style={{ border: form.categorie === k ? '2px solid #0A192F' : '2px solid #E5E5E0' }}>
                    <span className="text-xl block mb-1">{catIcons[k]}</span>
                    <span className="text-[10px] font-bold tracking-widest uppercase">{v}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold tracking-widest uppercase" style={{ color: '#7A828A' }}>Contenu</p>
              <div className="flex items-center gap-3">
                <span className="text-xs" style={{ color: '#7A828A' }}>
                  {form.contenu.length} caractères
                </span>
                <button type="button" className="text-xs font-bold tracking-widest uppercase"
                  onClick={() => setShowPreview(!showPreview)}
                  style={{ color: '#0A192F', textDecoration: 'underline' }}>
                  {showPreview ? 'Éditer' : 'Aperçu'}
                </button>
              </div>
            </div>

            {showPreview ? (
              <div className="p-4 min-h-[200px]" style={{ border: '2px solid #E5E5E0', backgroundColor: '#FAFAF6' }}>
                <h4 className="font-serif font-bold text-lg mb-2" style={{ color: '#0A192F' }}>
                  {catIcons[form.categorie]} {form.titre || 'Sans titre'}
                </h4>
                <div className="text-sm whitespace-pre-line" style={{ color: '#5A626A', lineHeight: '1.7' }}>
                  {form.contenu || 'Aucun contenu'}
                </div>
              </div>
            ) : (
              <div>
                <textarea className="input-root min-h-[200px] resize-y" value={form.contenu}
                  onChange={(e) => setForm({ ...form, contenu: e.target.value })}
                  placeholder="Rédigez le contenu de l'article ici..." required
                  style={{ lineHeight: '1.7' }} />
                {form.contenu.length > 0 && form.contenu.length < 50 && (
                  <p className="text-xs mt-1" style={{ color: '#D4A017' }}>Minimum 50 caractères recommandés</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 p-3" style={{ border: '2px solid #E5E5E0', backgroundColor: '#FAFAF6' }}>
            <input type="checkbox" id="estPublie" checked={form.estPublie}
              onChange={(e) => setForm({ ...form, estPublie: e.target.checked })}
              className="w-4 h-4" />
            <label htmlFor="estPublie" className="text-sm cursor-pointer" style={{ color: '#0A192F' }}>
              <strong>Publier immédiatement</strong>
              <span className="block text-xs mt-0.5" style={{ color: '#7A828A' }}>
                L'article sera visible par tous les utilisateurs
              </span>
            </label>
          </div>

          <div className="flex gap-3 pt-2" style={{ borderTop: '2px solid #E5E5E0' }}>
            <button type="button" className="btn-root flex-1 py-3 text-sm" onClick={onClose}
              style={{ borderColor: '#0A192F', color: '#0A192F' }}>
              Annuler
            </button>
            <button type="submit" className="btn-root flex-1 py-3 text-sm" disabled={loading || !form.titre.trim() || !form.contenu.trim()}
              style={{ backgroundColor: '#0A192F', color: '#F4F4F0', borderColor: '#0A192F', opacity: loading || !form.titre.trim() || !form.contenu.trim() ? 0.5 : 1 }}>
              {loading ? 'Publication...' : article ? 'Mettre à jour' : `Publier l'article`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MessagesSection() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  const fetchThreads = useCallback(async () => {
    try {
      const res = await messageAPI.getThreads();
      setThreads(res.data.data.threads || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);
  useAutoRefresh(fetchThreads, 30000);

  const openConversation = async (otherUserId) => {
    setActiveConv(otherUserId);
    try {
      const res = await messageAPI.getConversation(otherUserId);
      setMessages(res.data.data || []);
    } catch (err) { console.error(err); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    try {
      await messageAPI.send({ destinataireId: activeConv, contenu: newMsg });
      setNewMsg('');
      openConversation(activeConv);
      fetchThreads();
    } catch (err) { console.error(err); }
  };

  const openNewMessage = async () => {
    setShowNewModal(true);
    setUserSearch('');
    try {
      const res = await userAPI.getAll({ limit: 100 });
      setAllUsers(res.data.users || []);
    } catch {}
  };

  const startConversation = (otherUserId) => {
    setShowNewModal(false);
    openConversation(otherUserId);
  };

  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;
  const filteredUsers = allUsers.filter(u => u.id !== currentUserId && (!userSearch || u.nom.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase())));

  return (
    <div>
      <div className="mb-6"><h1 className="font-serif text-2xl font-bold text-navy">Messagerie</h1></div>
      <div className="card flex h-[600px] overflow-hidden relative">
        <div className="w-72 border-r border-parchment-dark overflow-y-auto flex-shrink-0">
          <div className="p-3 border-b border-parchment-dark flex items-center justify-between">
            <span className="font-medium text-navy text-sm">Conversations</span>
            <button onClick={openNewMessage} className="flex items-center gap-1 text-xs font-bold tracking-widest uppercase px-2.5 py-1 transition-colors"
              style={{ backgroundColor: '#0A192F', color: '#F4F4F0' }}>
              <FiPlus className="w-3 h-3" /> Nouveau
            </button>
          </div>
          {loading ? (
            <div className="p-4 text-center text-navy/40 text-sm">Chargement...</div>
          ) : threads.length === 0 ? (
            <div className="p-4 text-center text-navy/40 text-sm">Aucune conversation</div>
          ) : threads.map((t) => (
            <button key={t.otherUserId} onClick={() => openConversation(t.otherUserId)}
              className={`w-full text-left p-3 border-b border-parchment-dark/50 hover:bg-parchment/50 transition-colors ${activeConv === t.otherUserId ? 'bg-parchment' : ''}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
                  <FiUser className="w-4 h-4 text-navy/60" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-navy truncate">{t.otherUserName}</p>
                  <p className="text-xs text-navy/40 truncate">{t.lastMessage}</p>
                </div>
                {t.nonLu > 0 && <span className="bg-brass text-navy text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{t.nonLu}</span>}
              </div>
            </button>
          ))}
        </div>

        <div className="flex-1 flex flex-col">
          {activeConv ? (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => {
                  const isMe = m.expediteurId === JSON.parse(localStorage.getItem('user') || '{}').id;
                  return (
                    <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-md p-3 rounded-xl ${isMe ? 'bg-brass/10' : 'bg-parchment'}`}>
                        <p className="text-sm text-navy">{m.contenu}</p>
                        <p className="text-xs text-navy/40 mt-1">{new Date(m.dateEnvoi).toLocaleTimeString()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={handleSend} className="border-t border-parchment-dark p-3 flex gap-3">
                <input className="input-field flex-1" placeholder="Écrire un message..." value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)} />
                <button type="submit" className="btn-primary"><FiSend className="w-5 h-5" /></button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-navy/40">
              <div className="text-center"><FiMessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="text-sm">Sélectionnez une conversation</p></div>
            </div>
          )}
        </div>
      </div>

      {showNewModal && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 50 }}>
          <div className="bg-white shadow-lg w-96" style={{ maxHeight: '70%' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#E5E5E0' }}>
              <h3 className="font-bold text-sm" style={{ color: '#0A192F' }}>Nouveau message</h3>
              <button onClick={() => setShowNewModal(false)} style={{ color: '#7A828A' }}>✕</button>
            </div>
            <div className="p-3 border-b" style={{ borderColor: '#E5E5E0' }}>
              <input className="w-full px-3 py-2 text-sm border" placeholder="Rechercher un utilisateur..."
                style={{ borderColor: '#E5E5E0', outline: 'none' }}
                value={userSearch} onChange={(e) => setUserSearch(e.target.value)} autoFocus />
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
              {filteredUsers.length === 0 ? (
                <p className="text-sm text-center py-8" style={{ color: '#7A828A' }}>Aucun utilisateur trouvé</p>
              ) : filteredUsers.map(u => (
                <button key={u.id} onClick={() => startConversation(u.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-parchment"
                  style={{ borderBottom: '1px solid #F4F4F0' }}>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F4F4F0' }}>
                    <FiUser className="w-4 h-4" style={{ color: '#0A192F' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate" style={{ color: '#0A192F' }}>{u.nom}</p>
                    <p className="text-xs truncate" style={{ color: '#7A828A' }}>{u.email}</p>
                  </div>
                  <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 flex-shrink-0" style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
                    {u.role === 'student' ? 'Étudiant' : u.role === 'owner' ? 'Propriétaire' : u.role === 'admin' ? 'Admin' : 'Service'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationsSection() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const userData = JSON.parse(localStorage.getItem('user') || '{}');

  const fetch = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const res = await notificationAPI.getAll({ limit: 50 });
      setNotifs(res.data.notifications || []);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  useAutoRefresh(() => fetch(true), 30000);

  const handleMarkRead = async (id) => {
    try { await notificationAPI.markAsRead(id); fetch(); }
    catch (err) { console.error(err); }
  };

  const handleMarkAllRead = async () => {
    try { await notificationAPI.markAllAsRead(); fetch(); }
    catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    try { await notificationAPI.delete(id); fetch(); }
    catch (err) { console.error(err); }
  };

  const typeIcons = { info: '📌', demarche: '📋', message: '💬', alert: '⚠️' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-2xl font-bold text-navy">Notifications</h1></div>
        <button className="btn-outline btn-sm" onClick={handleMarkAllRead}>Tout marquer comme lu</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-brass border-t-transparent" /></div>
      ) : notifs.length === 0 ? (
        <div className="text-center py-12 text-navy/40">Aucune notification</div>
      ) : (
        <div className="space-y-2">
          {notifs.map((n) => (
            <div key={n.id} className={`card p-4 flex items-start gap-3 ${!n.estLu ? 'border-l-4 border-l-brass' : ''}`}>
              <span className="text-xl">{typeIcons[n.type] || '📌'}</span>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-navy text-sm">{n.titre}</p>
                    {n.message && <p className="text-xs text-navy/60 mt-0.5">{n.message}</p>}
                  </div>
                  <span className="text-xs text-navy/40 whitespace-nowrap ml-4">{new Date(n.dateCreation).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {!n.estLu && (
                    <button className="text-xs text-brass hover:underline" onClick={() => handleMarkRead(n.id)}>Marquer comme lu</button>
                  )}
                    <button className="text-xs text-red-500 hover:underline" onClick={() => handleDelete(n.id)}>Supprimer</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const stepTypeLabels = {
  visa: 'Visa étudiant', academic: 'Inscription académique',
  insurance: 'Assurance', residence_permit: 'Carte de séjour',
};

function RequirementsSection() {
  const [requirements, setRequirements] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editReq, setEditReq] = useState(null);

  const fetchRequirements = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = filterType ? { type: filterType } : {};
      const res = await requirementAPI.getAll(params);
      setRequirements(res.data.data);
    } catch (err) { showError('Erreur de chargement'); }
    finally { if (!skipLoading) setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchRequirements(); }, [fetchRequirements]);
  useAutoRefresh(() => fetchRequirements(true), 30000);

  const handleDelete = async (req) => {
    const confirmed = await confirmAction('Supprimer cette exigence ?', `"${req.titre}" sera définitivement supprimé.`);
    if (!confirmed) return;
    try {
      await requirementAPI.delete(req.id);
      showSuccess('Exigence supprimée');
      fetchRequirements();
    } catch (err) { showError('Erreur lors de la suppression'); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold" style={{ color: '#0A192F' }}>Exigences documentaires</h2>
          <p className="text-sm mt-1" style={{ color: '#7A828A' }}>
            Gérez les documents attendus pour chaque type de démarche
          </p>
        </div>
        <button className="btn-primary-root text-sm" onClick={() => { setEditReq(null); setShowModal(true); }}>
          <FiPlus className="mr-2" /> Nouvelle exigence
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <select className="input-root max-w-xs" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">Tous les types</option>
          {Object.entries(stepTypeLabels).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent mx-auto" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent', borderRadius: '50%' }} />
        </div>
      ) : requirements.length === 0 ? (
        <div className="text-center py-12" style={{ color: '#7A828A' }}>
          <FiFileText className="w-12 h-12 mx-auto mb-3" style={{ color: '#E5E5E0' }} />
          <p>Aucune exigence {filterType ? 'pour ce type' : ''}</p>
          <button className="btn-primary-root mt-4 text-sm" onClick={() => { setEditReq(null); setShowModal(true); }}>
            <FiPlus className="mr-2" /> Ajouter une exigence
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {requirements.map((req) => (
            <div key={req.id} className="card-root p-4 flex items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: '#0A192F' }}>{req.titre}</span>
                  {req.obligatoire && (
                    <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5"
                      style={{ backgroundColor: '#C59B6C', color: '#0A192F' }}>Obligatoire</span>
                  )}
                </div>
                <p className="text-xs mt-1" style={{ color: '#5A626A' }}>{req.description}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs" style={{ color: '#88929B' }}>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5"
                    style={{ backgroundColor: '#F4F4F0' }}>
                    {stepTypeLabels[req.type] || req.type}
                  </span>
                  <span>Formats : {req.formats}</span>
                  <span>Ordre : {req.ordre}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button className="btn-root text-xs px-3 py-1.5"
                  style={{ borderColor: '#0A192F', color: '#0A192F' }}
                  onClick={() => { setEditReq(req); setShowModal(true); }}>
                  <FiEdit2 className="w-3 h-3 mr-1" /> Modifier
                </button>
                <button className="btn-root text-xs px-3 py-1.5"
                  style={{ borderColor: '#C62828', color: '#C62828' }}
                  onClick={() => handleDelete(req)}>
                  <FiTrash2 className="w-3 h-3 mr-1" /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <RequirementFormModal
          requirement={editReq}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchRequirements(); }}
        />
      )}
    </div>
  );
}

function RequirementFormModal({ requirement, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: requirement?.type || 'visa',
    titre: requirement?.titre || '',
    description: requirement?.description || '',
    formats: requirement?.formats || 'PDF, JPG, PNG',
    obligatoire: requirement?.obligatoire ?? true,
    ordre: requirement?.ordre ?? 0,
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre.trim() || !form.description.trim()) {
      showError('Le titre et la description sont requis');
      return;
    }
    setSending(true);
    try {
      if (requirement) {
        await requirementAPI.update(requirement.id, form);
      } else {
        await requirementAPI.create(form);
      }
      showSuccess(requirement ? 'Exigence modifiée' : 'Exigence créée');
      onSaved();
    } catch (err) { showError(err.response?.data?.error || 'Erreur'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}
        style={{ border: '2px solid #0A192F', animation: 'slideUp 0.2s ease' }}>
        <div className="p-6" style={{ borderBottom: '2px solid #E5E5E0' }}>
          <div className="flex items-center justify-between">
            <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>
              {requirement ? 'Modifier l\'exigence' : 'Nouvelle exigence'}
            </h3>
            <button className="text-2xl" style={{ color: '#7A828A' }} onClick={onClose}>✕</button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label-root mb-1.5">Type de démarche</label>
            <select className="input-root" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {Object.entries(stepTypeLabels).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-root mb-1.5">Titre *</label>
            <input className="input-root" value={form.titre}
              onChange={(e) => setForm({ ...form, titre: e.target.value })}
              placeholder="Ex: Passeport valide" required />
          </div>
          <div>
            <label className="label-root mb-1.5">Description *</label>
            <textarea className="input-root" rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Décrivez le document attendu, les conditions..." required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-root mb-1.5">Formats acceptés</label>
              <input className="input-root" value={form.formats}
                onChange={(e) => setForm({ ...form, formats: e.target.value })}
                placeholder="PDF, JPG, PNG" />
            </div>
            <div>
              <label className="label-root mb-1.5">Ordre d'affichage</label>
              <input type="number" className="input-root" min="0" value={form.ordre}
                onChange={(e) => setForm({ ...form, ordre: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="obligatoire" checked={form.obligatoire}
              onChange={(e) => setForm({ ...form, obligatoire: e.target.checked })}
              className="w-4 h-4" style={{ accentColor: '#C59B6C' }} />
            <label htmlFor="obligatoire" className="text-sm font-medium" style={{ color: '#0A192F' }}>
              Document obligatoire
            </label>
          </div>
          <div className="flex gap-3 pt-2" style={{ borderTop: '2px solid #E5E5E0' }}>
            <button type="button" className="btn-root flex-1 py-3 text-sm"
              style={{ borderColor: '#0A192F', color: '#0A192F' }} onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-root flex-1 py-3 text-sm" disabled={sending}
              style={{ backgroundColor: '#0A192F', color: '#F4F4F0', borderColor: '#0A192F' }}>
              {sending ? 'Traitement...' : requirement ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
